import React, { useContext, useMemo, useRef, useState } from 'react'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import listPlugin from '@fullcalendar/list'
import moment from 'moment'
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import API from '../../services/API'
import AddIcon from '@mui/icons-material/Add'
import { useLocation, useNavigate } from 'react-router-dom'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import { TenantSettingsContext, UserContext } from '../../App'
import { getStatusColourConfig } from '../../helpers/getEventStatusColor'
import { Person, PersonOff, RemoveCircleOutline } from '@mui/icons-material'
import useIsMobile from '../../helpers/useIsMobile'
import './Calendar.css'

const VIEW_OPTIONS = [
  { key: 'dayGridMonth', label: 'Month' },
  { key: 'dayGridWeek', label: 'Week' },
  { key: 'dayGridDay', label: 'Day' },
  { key: 'listMonth', label: 'List' },
]

// Both list views (listWeek deep links included) light up the List segment.
const activeViewKey = (view) => (view?.startsWith('list') ? 'listMonth' : view)

// Shift-cover marker shown inside each event block. The shape carries the
// meaning (person / person-off / circle-minus); inside a status-coloured
// block the icon inherits the block's contrast text colour, while the legend
// shows the coloured versions.
const CoverageIcon = ({ event, inherit = false }) => {
  if (!event.totalShifts)
    return (
      <Tooltip title="No shifts yet">
        <RemoveCircleOutline
          sx={{ fontSize: 13, color: inherit ? 'inherit' : '#94A3B8' }}
        />
      </Tooltip>
    )
  if (event.assignedShifts >= event.totalShifts)
    return (
      <Tooltip title="All shifts filled">
        <Person
          color={inherit ? 'inherit' : 'success'}
          sx={{ fontSize: 13 }}
        />
      </Tooltip>
    )
  return event.unassignedShifts ? (
    <Tooltip title="Shifts uncovered">
      <PersonOff
        color={inherit ? 'inherit' : 'error'}
        sx={{ fontSize: 13 }}
      />
    </Tooltip>
  ) : null
}

const LegendDot = ({ color }) => (
  <Box
    sx={{
      width: 7,
      height: 7,
      borderRadius: '50%',
      bgcolor: color,
      flexShrink: 0,
    }}
  />
)

export function Calendar() {
  const calendarRef = useRef()
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const isMobile = useIsMobile()
  // A 7-column month grid is unreadable on a phone, so phones default to a
  // month list unless the URL pins a specific view. Desktop keeps the grid.
  const defaultView = isMobile ? 'listMonth' : 'dayGridMonth'
  const [loading, setLoading] = useState(true)
  const [events, setEvents] = useState([])
  const [isTodayInView, setIsTodayInView] = useState(false)
  const [viewTitle, setViewTitle] = useState('')
  const [calendarView, setCalendarView] = useState(
    queryParams.get('view') || defaultView,
  )
  const [showCancelledEvents, setShowCancelledEvents] = useState(false)
  const navigate = useNavigate()

  const changeCalendarView = (view) => {
    const api = calendarRef.current?.getApi()
    if (!api) return

    api.changeView(view)
    setCalendarView(view)
  }

  const startDateFromUrl = queryParams.get('start')
  const endDateFromUrl = queryParams.get('end')

  const { user } = useContext(UserContext)
  const { orgConfig } = useContext(TenantSettingsContext)

  const statusColours =
    orgConfig?.orgSettings?.eventPlanningSettings?.statuses || {}

  const isReadOnly =
    !user?.security?.isSiteAdmin &&
    !user?.employee?.teams?.some((team) => ['Event Planning'].includes(team))

  const visibleEvents = useMemo(
    () =>
      events.filter((event) =>
        !showCancelledEvents ? event.status !== 'Cancelled' : true,
      ),
    [events, showCancelledEvents],
  )

  const gapCount = visibleEvents.filter(
    (event) =>
      event.totalShifts &&
      event.unassignedShifts &&
      event.assignedShifts < event.totalShifts,
  ).length

  const legendStatuses = useMemo(
    () =>
      [...new Set(visibleEvents.map((event) => event.status).filter(Boolean))].map(
        (status) => ({
          status,
          color: getStatusColourConfig(status, statusColours).main,
        }),
      ),
    [visibleEvents, statusColours],
  )

  const calendarEvents = visibleEvents.map((event) => {
    const statusColor = getStatusColourConfig(event.status, statusColours)

    return {
      title: event.title,
      start: moment(event.startDate).format('YYYY-MM-DDTHH:mm'),
      end: moment(event.endDate).format('YYYY-MM-DDTHH:mm'),
      // Status colour fills the whole block (a customer feature request) —
      // FullCalendar applies these inline, and borderColor also colours the
      // list views' leading dot.
      backgroundColor: statusColor.main,
      borderColor: statusColor.main,
      textColor: statusColor.contrast,
      statusColor: statusColor.main,
      totalShifts: event.totalShifts,
      assignedShifts: event.assignedShifts,
      unassignedShifts: event.unassignedShifts,
      id: event._id,
    }
  })

  const getEvents = (start, end) => {
    setLoading(true)
    API.post(`/events`, {
      start,
      end,
    })
      .then((res) => {
        const datesAr = res.data.map((item) => {
          item.start = item.startDate
          item.end = item.endDate
          return item
        })
        setEvents(datesAr)
      })
      .catch((e) => console.log(e))
      .finally(() => setLoading(false))
  }

  const summaryCaption = loading
    ? 'Loading events...'
    : visibleEvents.length === 0
      ? 'No events in this range'
      : `${visibleEvents.length} event${visibleEvents.length === 1 ? '' : 's'}${
          gapCount
            ? ` · ${gapCount} with shift gap${gapCount === 1 ? '' : 's'}`
            : ''
        }`

  return (
    // The calendar sizes to its content (height="auto") and the page
    // scrolls — no fixed-height flex chain here, which would clip the lower
    // week rows behind the card's rounded-corner overflow:hidden.
    <Stack>
      <Paper
        sx={{
          borderRadius: 3,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          gap={1.5}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', lg: 'center' }}
          sx={{ p: 2, borderBottom: '1px solid #EEF2F6' }}
        >
          <Box textAlign={{ xs: 'center', lg: 'left' }}>
            <Typography
              sx={{
                fontSize: 20,
                fontWeight: 600,
                color: '#242F40',
                lineHeight: 1.2,
              }}
            >
              {viewTitle}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {summaryCaption}
            </Typography>
          </Box>
          <Stack
            direction="row"
            gap={1}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
            justifyContent={{ xs: 'center', lg: 'flex-end' }}
          >
            <Stack
              direction="row"
              alignItems="stretch"
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '5px',
                overflow: 'hidden',
                bgcolor: 'background.paper',
              }}
            >
              <ButtonBase
                aria-label="Previous"
                sx={{ width: 34, height: 32, color: 'text.secondary' }}
                onClick={() => calendarRef.current.getApi().prev()}
              >
                <ChevronLeftIcon fontSize="small" />
              </ButtonBase>
              <ButtonBase
                disabled={isTodayInView}
                sx={{
                  px: 1.5,
                  borderLeft: '1px solid',
                  borderRight: '1px solid',
                  borderColor: 'divider',
                  fontFamily: 'inherit',
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: isTodayInView ? 'text.disabled' : 'text.primary',
                }}
                onClick={() => {
                  calendarRef.current.getApi().today()
                  const calendarApi = calendarRef.current.getApi()
                  const view = calendarApi.view
                  const today = new Date()
                  setIsTodayInView(
                    today >= new Date(view.activeStart) &&
                      today < new Date(view.activeEnd),
                  )
                }}
              >
                Today
              </ButtonBase>
              <ButtonBase
                aria-label="Next"
                sx={{ width: 34, height: 32, color: 'text.secondary' }}
                onClick={() => calendarRef.current.getApi().next()}
              >
                <ChevronRightIcon fontSize="small" />
              </ButtonBase>
            </Stack>
            <Stack
              direction="row"
              alignItems="center"
              spacing="2px"
              sx={{ bgcolor: 'anMuted.main', borderRadius: '6px', p: '3px' }}
            >
              {VIEW_OPTIONS.map((option) => {
                const active = activeViewKey(calendarView) === option.key
                return (
                  <ButtonBase
                    key={option.key}
                    onClick={() => changeCalendarView(option.key)}
                    sx={{
                      px: 1.5,
                      py: 0.55,
                      borderRadius: '4px',
                      fontFamily: 'inherit',
                      fontSize: 12.5,
                      fontWeight: active ? 600 : 500,
                      color: active ? 'text.primary' : 'text.secondary',
                      bgcolor: active ? 'background.paper' : 'transparent',
                      boxShadow: active
                        ? '0 1px 2px rgba(15, 23, 42, 0.08)'
                        : 'none',
                    }}
                  >
                    {option.label}
                  </ButtonBase>
                )
              })}
            </Stack>
            <Tooltip
              title={
                showCancelledEvents
                  ? 'Hide cancelled events'
                  : 'Show cancelled events'
              }
            >
              <IconButton
                size="small"
                color={showCancelledEvents ? 'anGreen' : 'default'}
                onClick={() => setShowCancelledEvents((prev) => !prev)}
              >
                {showCancelledEvents ? (
                  <Visibility fontSize="small" />
                ) : (
                  <VisibilityOff fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
            {!isReadOnly && (
              <Button
                size={isMobile ? 'small' : 'medium'}
                color="primary"
                variant="contained"
                onClick={() => navigate('/events/add-new')}
                startIcon={<AddIcon />}
              >
                Add Event
              </Button>
            )}
          </Stack>
        </Stack>
        {loading && <LinearProgress />}
        {!loading && events.length === 0 && !isReadOnly && (
          <Alert
            severity="info"
            sx={{ m: 2, mb: 0 }}
            action={
              <Button
                size="small"
                color="inherit"
                onClick={() => navigate('/events/add-new')}
              >
                Create an Event
              </Button>
            }
          >
            No events in this date range yet. Events hold your medical plans,
            shifts and invoicing — create one to start planning cover.
          </Alert>
        )}
        <Box className="an-calendar" sx={{ px: 1.5, pt: 1, pb: 1.5 }}>
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, listPlugin, interactionPlugin]}
            eventDisplay="block"
            datesSet={(dates) => {
              const startUnix = moment(dates.view.currentStart).unix()
              const endUnix = moment(dates.view.currentEnd).unix()

              if (
                !startDateFromUrl ||
                !endDateFromUrl ||
                startUnix !== parseInt(startDateFromUrl) ||
                endUnix !== parseInt(endDateFromUrl)
              ) {
                navigate(
                  `${location.pathname}?start=${startUnix}&end=${endUnix}&view=${dates.view.type}`,
                  { replace: true },
                )
              }

              getEvents(dates.start, dates.end)

              const today = new Date()
              setIsTodayInView(
                today >= new Date(dates.view.activeStart) &&
                  today < new Date(dates.view.activeEnd),
              )

              setViewTitle(dates.view.title)
              setCalendarView(dates.view.type)
            }}
            startParam="start"
            endParam="end"
            initialView={queryParams.get('view') || defaultView}
            initialDate={
              startDateFromUrl
                ? moment.unix(startDateFromUrl).format('YYYY-MM-DD')
                : undefined
            }
            nextDayThreshold="04:00"
            weekends={true}
            displayEventTime={false}
            firstDay={1}
            height="auto"
            events={calendarEvents}
            eventClick={(event) =>
              isReadOnly
                ? navigate(`/event-info/${event.event.id}`)
                : navigate(`/events/${event.event.id}`)
            }
            headerToolbar={false}
            eventContent={(arg) => {
              const event = arg.event.extendedProps
              // Grid cells are narrow: clip long titles instead of letting
              // them poke past the day cell. Grid blocks are status-coloured
              // so text/icons inherit FullCalendar's contrast textColor; list
              // views show the status as their leading dot instead.
              const isGridView = arg.view.type.startsWith('dayGrid')

              return (
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={0.5}
                  px={0.25}
                  minWidth={0}
                  overflow={isGridView ? 'hidden' : 'visible'}
                  sx={{ color: 'inherit' }}
                >
                  <CoverageIcon event={event} inherit={isGridView} />
                  <Typography
                    variant="caption"
                    fontWeight={600}
                    color={isGridView ? 'inherit' : 'text.primary'}
                    noWrap={isGridView}
                    sx={{ minWidth: 0 }}
                  >
                    {arg.event.title}
                  </Typography>
                </Stack>
              )
            }}
          />
        </Box>
        {!loading && visibleEvents.length > 0 && (
          <Stack
            direction="row"
            alignItems="center"
            gap={2}
            flexWrap="wrap"
            useFlexGap
            sx={{ px: 2, py: 1, borderTop: '1px solid #EEF2F6' }}
          >
            {legendStatuses.map((item) => (
              <Stack
                key={item.status}
                direction="row"
                alignItems="center"
                gap={0.75}
              >
                <LegendDot color={item.color} />
                <Typography variant="caption" color="text.secondary">
                  {item.status}
                </Typography>
              </Stack>
            ))}
            <Box sx={{ width: '1px', height: 14, bgcolor: 'divider' }} />
            <Stack direction="row" alignItems="center" gap={0.75}>
              <Person color="success" sx={{ fontSize: 13 }} />
              <Typography variant="caption" color="text.secondary">
                Shifts covered
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" gap={0.75}>
              <PersonOff color="error" sx={{ fontSize: 13 }} />
              <Typography variant="caption" color="text.secondary">
                Shift gaps
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" gap={0.75}>
              <RemoveCircleOutline sx={{ fontSize: 13, color: '#94A3B8' }} />
              <Typography variant="caption" color="text.secondary">
                No shifts yet
              </Typography>
            </Stack>
          </Stack>
        )}
      </Paper>
    </Stack>
  )
}

export default Calendar
